/**
 * Phase 6 — Export RZL.
 *
 * Production code path:
 *  - POST /api/monthly-batches/:month → buildRzlCsv (hub/src/export/rzl.ts)
 *  - Generiert .csv-Datei mit Sheet pro Mitarbeiter (RZL-Format)
 *  - Setzt state=exported für alle inkludierten Karten
 *  - Liefert Datei als Download (Content-Disposition: attachment)
 *
 * Output dieser Phase = finales Lieferobjekt:
 *   - .csv-Datei
 *   - state=exported
 */
import { useEffect, useState } from "react";
import type { WerkstattCardContext } from "../../pages/WerkstattPage";
import { api, type Card } from "../../lib/api";
import { PipelineStepList, type PipelineStep } from "../SettingsTestOcr";
import { PhaseHeader, withUuids } from "./Phase1Upload";

export function Phase6Export({ ctx }: { ctx: WerkstattCardContext }) {
  const month = ctx.card?.month ?? new Date().toISOString().slice(0, 7);
  const clientId = ctx.card?.client_id ?? "default";
  const [status, setStatus] = useState<any | null>(null);
  const [monthCards, setMonthCards] = useState<Card[]>([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    fetch(`api/monthly-batches/${month}/status?client_id=${clientId}`, { credentials: "same-origin" })
      .then(r => r.json())
      .then(setStatus)
      .catch(e => setErr(e.message));
    // Live-Liste aller exportierbaren Karten des Monats für Step 4 Content-Inspector.
    // Reicht für Birgit ohne extra Endpoint: kanban /api/cards filtert nicht nach state.
    api.cards.list(month, "", clientId)
      .then(r => setMonthCards(r.cards.filter(c =>
        c.state === "calculated" || c.state === "exported"
      )))
      .catch(() => { /* silent */ });
  }, [month, clientId]);

  const c = ctx.card;
  const calcCount = status?.counts?.calculated ?? 0;
  const expCount = status?.counts?.exported ?? 0;

  async function triggerExport(opts?: { includeExported?: boolean }) {
    const includeExported = opts?.includeExported === true;
    const verb = includeExported ? "Re-Export inkl. bereits exportierter" : "Export";
    const willExport = includeExported ? calcCount + expCount : calcCount;
    if (!confirm(`${verb} für ${month}?\n→ ${willExport} Karten in CSV` +
      (includeExported ? "\n→ KEIN State-Change (bereits exported bleibt exported)" : "\n→ state=calculated wird auf exported gesetzt"),
    )) return;
    setBusy(true);
    setErr(null);
    try {
      await api.export.download(month, clientId, { includeExported });
      ctx.reload();
    } catch (e: any) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  }

  const steps: PipelineStep[] = [
    {
      num: 1,
      title: "Export-Readiness prüfen",
      description:
        "Karten im state=calculated werden in den **Default-Export** aufgenommen — "
        + "Karten bereits in state=exported nur in den **Re-Export** (kein State-Change). "
        + "Karten in früheren States (uploaded/ocr_*/validated) werden übersprungen. "
        + "Server-Truth: hub/src/routes/export.ts filtert nach gewähltem Modus, "
        + "Default-POST liefert 409 wenn calculated=0.",
      // Status — Readiness-Check, KEIN Fehler-Detektor:
      //  done    = mindestens ein Export-Pfad verfügbar (calc>0 für Default,
      //            exp>0 für Re-Export) → User kann etwas tun
      //  pending = weder calc noch exp Karten — Monat hat noch keine Output-
      //            fähigen Karten produziert (passiert in früheren Phasen)
      // KEIN error: Skipped Karten in früheren States sind kein Phase-6-Problem,
      // sie gehören zu Phase 1/2/3/4. "calc=0 + exp>0" ist ein VALIDER Zustand
      // (Monat bereits abgeschlossen, Re-Export möglich für Audit).
      status: !status ? "pending"
            : calcCount > 0 || expCount > 0 ? "done"
            : "pending",
      detail: !status ? "(lädt)" : (
        <>
          <strong style={{ color: calcCount > 0 ? "#15803d" : "#92400e" }}>
            {calcCount} calculated → Default-Export
          </strong>
          {expCount > 0 && <> · <span style={{ color: "#6b7280" }}>{expCount} exported → nur Re-Export</span></>}
          {(status.total - calcCount - expCount) > 0 && (
            <> · <span style={{ color: "#92400e" }}>
              {status.total - calcCount - expCount} werden übersprungen (nicht berechnet)
            </span></>
          )}
        </>
      ),
    },
    {
      num: 2,
      title: "RZL-CSV bauen",
      description:
        "buildRzlCsv() in hub/src/export/rzl.ts produziert UTF-8-CSV mit BOM. "
        + "Eine Zeile pro Lohnart pro Mitarbeiter (Normalstunden, Mehrstunden→ZA, "
        + "Überstunden 50% routed via pay_mode, K/U/F-Tage, Urlaubs-Bereiche). "
        + "Separator ';', Decimal-Komma — deutsche Excel-Konvention.",
      status: c?.state === "exported" ? "done" : "pending",
      detail: "buildRzlCsv(month, timesheets, expenses) → Buffer (CSV, ;-separated, UTF-8 BOM)",
    },
    {
      num: 3,
      title: "State-Transition calculated → exported",
      description:
        "Im selben Request: UPDATE timesheets/expenses SET state='exported' WHERE id IN (...). "
        + "Karten verschwinden danach aus dem Kanban (außer Filter zeigt exported).",
      status: c?.state === "exported" ? "done" : "pending",
      detail: c ? `Aktuelle Karte: state=${c.state}` : "(keine Karte)",
    },
    {
      num: 4,
      title: "Download zum User (Monats-Batch)",
      description:
        "Content-Disposition: attachment, filename `sibit-rzl-<client>-<month>-<timestamp>.csv`. "
        + "Der Download enthält ALLE exportierbaren Karten des Monats (calculated → "
        + "im aktuellen Export, exported → nur bei Re-Export inkludiert). "
        + "Status zeigt den Monats-Stand, nicht den State dieser einzelnen Karte — "
        + "Birgit lädt einmal pro Monats-Lauf für RZL.",
      // Status spiegelt den GANZEN Monats-Stand:
      //  done = alle exportierbaren bereits exported (Monat abgeschlossen)
      //  pending = es gibt noch calculated Karten die noch nicht exportiert wurden
      status: !status ? "pending"
            : (status.counts?.calculated ?? 0) === 0 && (status.counts?.exported ?? 0) > 0 ? "done"
            : "pending",
      detail: !status ? "(lädt)" : (
        <Phase6ContentInspector status={status} cards={monthCards} month={month} />
      ),
      optional: true,
    },
  ];

  const isExpense = c?.kind === "expense" || c?.kind === "unknown";
  return (
    <div>
      <PhaseHeader
        num={6}
        title="Export RZL"
        purpose="Aggregierte Monats-Daten als .csv für RZL-Lohnsoftware"
        inputs="alle calculated Karten (Stundenliste + Diäten) + employee.dienstnehmernummer"
        outputs="sibit-rzl-<client>-<month>.csv + state=exported"
        prodFiles={[
          "hub/src/routes/export.ts (POST /api/monthly-batches/:month)",
          "hub/src/export/rzl.ts (buildRzlCsv — kind-aware Spalten-Mapping geplant)",
        ]}
        userGates="(1) Bestätigung Export-Knopf  (2) Datei lokal speichern"
      />
      {isExpense && (
        <div style={{
          background: "#dbeafe", border: "1px solid #93c5fd", padding: "8px 12px",
          borderRadius: 4, marginBottom: 12, fontSize: 13,
        }}>
          <strong>Aktive Karte ist Diäten</strong> — Diäten-Karten landen im selben Monats-
          CSV wie Stundenliste-Karten, aber mit eigenen RZL-Konten (Diäten Inland steuerfrei,
          Diäten Ausland, Kilometergeld). Das kind-aware Spalten-Mapping in
          <code> buildRzlCsv</code> ist noch nicht implementiert — derzeit ignoriert der
          Export expense-Karten. Diäten-Export ist nächste Phase.
        </div>
      )}
      <PipelineStepList steps={withUuids(c?.id ?? null, "export", steps)} />
      <DownloadCard
        month={month}
        busy={busy}
        calcCount={calcCount}
        expCount={expCount}
        err={err}
        onExport={() => triggerExport()}
        onReExport={() => triggerExport({ includeExported: true })}
      />
    </div>
  );
}

/**
 * Zeigt den exakten Inhalt des Monats-Batch-Downloads: jede exportierbare Karte
 * (calculated ODER exported) mit Mitarbeiter, Stunden, state. Daten kommen aus
 * api.cards.list — gleicher Code-Pfad wie Kanban, kein Drift.
 */
function Phase6ContentInspector({
  status, cards, month,
}: {
  status: { total: number; counts: Record<string, number>; ready_for_export: number };
  cards: Card[];
  month: string;
}) {
  const calc = status.counts?.calculated ?? 0;
  const exp = status.counts?.exported ?? 0;
  const skip = status.total - calc - exp;
  // Sortiere: calculated (going-to-export) zuerst, dann exported (Audit-Zeile).
  const sorted = [...cards].sort((a, b) => {
    if (a.state !== b.state) return a.state === "calculated" ? -1 : 1;
    const an = `${a.last_name ?? ""} ${a.first_name ?? ""}`.trim();
    const bn = `${b.last_name ?? ""} ${b.first_name ?? ""}`.trim();
    return an.localeCompare(bn);
  });
  return (
    <div>
      <div style={{ marginBottom: 6 }}>
        Monat <strong>{month}</strong>:
        {" "}<span style={{ color: "#15803d" }}>{calc} calculated</span>
        {exp > 0 && <> · <span style={{ color: "#6b7280" }}>{exp} bereits exported</span></>}
        {skip > 0 && <> · <span style={{ color: "#92400e" }}>{skip} werden übersprungen (nicht berechnet)</span></>}
      </div>
      {sorted.length > 0 ? (
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11, background: "white", border: "1px solid #e5e7eb" }}>
          <thead>
            <tr style={{ background: "#f3f4f6", borderBottom: "1px solid #e5e7eb" }}>
              <th style={{ padding: "3px 6px", textAlign: "left" }}>Mitarbeiter</th>
              <th style={{ padding: "3px 6px", textAlign: "right" }}>Ist h</th>
              <th style={{ padding: "3px 6px", textAlign: "right" }}>Mehrst h</th>
              <th style={{ padding: "3px 6px", textAlign: "right" }}>Üst 50% h</th>
              <th style={{ padding: "3px 6px", textAlign: "left" }}>State</th>
              <th style={{ padding: "3px 6px", textAlign: "left" }}>Im Export?</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map(c => {
              const t = (c.data as any)?.calculation?.totals ?? {};
              const inExport = c.state === "calculated";
              return (
                <tr key={c.id} style={{ borderBottom: "1px solid #f3f4f6", opacity: inExport ? 1 : 0.6 }}>
                  <td style={{ padding: "3px 6px" }}>
                    {c.last_name ? `${c.last_name}, ${c.first_name}` : <em>(unzugeordnet)</em>}
                  </td>
                  <td style={{ padding: "3px 6px", textAlign: "right" }}>{t.stunden_ist ?? "—"}</td>
                  <td style={{ padding: "3px 6px", textAlign: "right" }}>{t.mehrstunden_total_h ?? "—"}</td>
                  <td style={{ padding: "3px 6px", textAlign: "right" }}>{t.ueberstunden_50_total_h ?? "—"}</td>
                  <td style={{ padding: "3px 6px" }}><code>{c.state}</code></td>
                  <td style={{ padding: "3px 6px", color: inExport ? "#15803d" : "#6b7280" }}>
                    {inExport ? "→ wird exportiert" : "✓ schon exported (nur Re-Export)"}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      ) : (
        <div className="muted" style={{ fontSize: 11 }}>Keine exportierbaren Karten in diesem Monat.</div>
      )}
    </div>
  );
}

/**
 * Hübsche Download-Card am Footer von Phase 6. Zwei mögliche Aktionen:
 *  - Default-Export (calculated → exported) — primary blue, immer dann sichtbar
 *    wenn ≥1 calculated existiert
 *  - Re-Export (inkl. bereits exported) — sekundär, sichtbar wenn ≥1 exported
 *    existiert. Kein State-Change.
 *
 * Status-driven Buttons: zeigt nur, was im aktuellen Monats-Stand möglich ist.
 */
function DownloadCard({
  month, busy, calcCount, expCount, err, onExport, onReExport,
}: {
  month: string;
  busy: boolean;
  calcCount: number;
  expCount: number;
  err: string | null;
  onExport: () => void;
  onReExport: () => void;
}) {
  const canExport = calcCount > 0;
  const canReExport = expCount > 0;
  return (
    <div style={{
      marginTop: 20,
      padding: 16,
      background: "linear-gradient(180deg, #eff6ff 0%, #f0f9ff 100%)",
      border: "1px solid #93c5fd",
      borderRadius: 8,
    }}>
      <div style={{
        fontSize: 12, color: "#1e40af", fontWeight: 600, marginBottom: 12,
        textTransform: "uppercase", letterSpacing: 0.4,
      }}>
        Download · Monat {month}
      </div>

      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "stretch" }}>
        {/* Default-Export */}
        <button
          type="button"
          onClick={onExport}
          disabled={busy || !canExport}
          title={
            busy ? "Bitte warten…"
              : !canExport ? "Keine calculated Karten — nichts neu zu exportieren"
                : `${calcCount} neue Karte${calcCount === 1 ? "" : "n"} exportieren und auf exported setzen`
          }
          style={{
            padding: "10px 18px",
            fontSize: 14, fontWeight: 600,
            background: !canExport || busy ? "#9ca3af" : "#1d4ed8",
            color: "white",
            border: 0, borderRadius: 6,
            cursor: !canExport || busy ? "not-allowed" : "pointer",
            display: "inline-flex", alignItems: "center", gap: 8,
            boxShadow: !canExport || busy ? "none" : "0 1px 3px rgba(29, 78, 216, 0.3)",
            transition: "all 0.15s",
          }}
        >
          <span style={{ fontSize: 18 }}>📊</span>
          <span style={{ display: "flex", flexDirection: "column", textAlign: "left" }}>
            <span>{busy ? "Exportiere…" : `Export · ${calcCount} Karten`}</span>
            <span style={{ fontSize: 11, fontWeight: 400, opacity: 0.85 }}>
              {busy ? "" : canExport ? "calculated → exported · CSV-Download" : "keine calculated Karten"}
            </span>
          </span>
        </button>

        {/* Re-Export — separate sekundäre Aktion */}
        {canReExport && (
          <button
            type="button"
            onClick={onReExport}
            disabled={busy}
            title={`Erneuter Export inkl. ${expCount} bereits exportierter Karten — kein State-Change`}
            style={{
              padding: "10px 18px",
              fontSize: 14, fontWeight: 600,
              background: "white",
              color: "#1e40af",
              border: "1px solid #93c5fd",
              borderRadius: 6,
              cursor: busy ? "wait" : "pointer",
              display: "inline-flex", alignItems: "center", gap: 8,
              transition: "all 0.15s",
            }}
          >
            <span style={{ fontSize: 18 }}>🔁</span>
            <span style={{ display: "flex", flexDirection: "column", textAlign: "left" }}>
              <span>Erneut · {calcCount + expCount} Karten</span>
              <span style={{ fontSize: 11, fontWeight: 400, opacity: 0.8 }}>
                inkl. {expCount} bereits exported · kein State-Change
              </span>
            </span>
          </button>
        )}
      </div>

      {err && (
        <div style={{
          marginTop: 12, padding: 8, borderRadius: 4,
          background: "#fef2f2", border: "1px solid #fca5a5",
          color: "#991b1b", fontSize: 12,
        }}>
          Fehler: {err}
        </div>
      )}
    </div>
  );
}
